// Sidebar search input + 4-chip status filter pill bar (JobsSection scope).
//
// "/" focuses this input (preventDefault stops the browser quick-find), the
// GitHub/Slack convention. Ctrl/Cmd+K is DELIBERATELY NOT bound here — that key
// belongs solely to the command palette (AppShell useShortcut OPEN_PALETTE);
// binding both produced a double-fire collision. The "/" listener is guarded by
// isTypingTarget() so a slash typed inside the composer or any field types
// normally instead of being hijacked to focus the search box.
//
// Fully controlled component: parent JobsSection owns query + statusFilter
// state so the filter pipeline can run client-side substring match on the
// already-fetched jobs list without a debounce (<200 items, instant feel).

import { useEffect, useRef } from "react";

import { isInsideTiptap } from "@/lib/useShortcut";

// True when focus is in a field where "/" should type normally, not focus search.
function isTypingTarget(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.activeElement;
  if (el instanceof HTMLElement) {
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable) {
      return true;
    }
  }
  return isInsideTiptap();
}

export type StatusFilter = "all" | "active" | "completed" | "failed";

interface JobsSearchInputProps {
  query: string;
  onQueryChange: (q: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
}

const FILTER_CHIPS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

export function JobsSearchInput({
  query,
  onQueryChange,
  statusFilter,
  onStatusFilterChange,
}: JobsSearchInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget()) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex flex-col gap-2 border-b border-[var(--border-default)] px-3 py-2">
      <form onSubmit={(e) => e.preventDefault()} className="relative">
        <svg
          width="13"
          height="13"
          viewBox="0 0 14 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"
        >
          <circle cx="6" cy="6" r="4" />
          <path d="M11 11L9 9" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          aria-label="Search threads"
          placeholder="Search threads… (/)"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onQueryChange("");
            }
          }}
          className="type-body-sm w-full rounded-[var(--radius-md)] bg-[var(--surface-raised)] py-1 pl-7 pr-2 text-[var(--text-primary)] shadow-[var(--elev-ring)] outline-none placeholder:text-[var(--text-tertiary)] focus-visible:shadow-[var(--elev-focus)]"
        />
      </form>
      {/* biome-ignore lint/a11y/useSemanticElements: fieldset would impose form-control semantics + default browser styling on a filter pill bar */}
      <div role="group" aria-label="Filter by status" className="flex flex-wrap items-center gap-1.5">
        {FILTER_CHIPS.map((chip) => {
          const isActive = statusFilter === chip.value;
          const chipClass = isActive
            ? "bg-[var(--accent-primary)] text-[var(--text-on-accent)]"
            : "bg-transparent text-[var(--text-secondary)] ring-1 ring-[var(--border-default)] hover:text-[var(--text-primary)] hover:ring-[var(--border-strong)]";
          return (
            <button
              key={chip.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => onStatusFilterChange(chip.value)}
              className={`type-caption rounded-[var(--radius-pill)] px-2.5 py-0.5 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] focus-visible:outline-none focus-visible:shadow-[var(--elev-focus)] ${chipClass}`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
